import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import { Card, Flex, Heading, Spacer, Td, Text, Tr } from "@chakra-ui/react";
import TableView from "../../components/TableView";
import { useSelector } from "react-redux";
import CustomButton from "../../components/ButtonComp";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";
import {
  get_all_inventory_list,
  get_inventory_list,
} from "../../redux/inventorySlice";
import { get_all_vendor_list } from "../../redux/vendorSlice";
import CustomSingleInput from "../../components/CustomSingleInput";
import Loader from "../../components/Loader";
import { baseUrl } from "../../utils/utils";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const PurchaseReports = () => {
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [total_count, setTotal_count] = useState(0);
  const [salesType, setSalesType] = useState("");
  const [reports_list, setReports_list] = useState([]);
  const [loading, setLoading] = useState(false);
  const { all_inventory_list } = useSelector((state) => state.inventory);
  const { all_vendor_list } = useSelector((state) => state.vendors);
  const [selectedItem, setSelectedItem] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchParams, setSearchParams] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // ✅ Added for Excel download
  const [excelUrl, setExcelUrl] = useState(null);

  const vendor_list = all_vendor_list?.map((v) => ({
    label: v?.tradename,
    value: v?.id,
  }));

  const product_list = all_inventory_list?.map((v) => ({
    label: v?.product_name,
    value: v?.uuid,
  }));

  const get_report_list = async () => {
    try {
      setLoading(true);
      const body = new FormData();

      body.append("action", salesType || "list");
      body.append("page", page);
      body.append("limit", rows);
      body.append("fd", searchParams.startDate);
      body.append("td", searchParams.endDate);

      if (salesType === "product") {
        body.append("prod_code", searchParams.selectedItem);
      }
      if (salesType === "vendor") {
        body.append("vendor_id", searchParams.selectedItem);
      }

      const response = await fetch(`${baseUrl}reports/purchase_report.php`, {
        method: "POST",
        body,
      });

      const res = await response.json();
      if (res?.success) {
        setReports_list(res?.list);
        setTotal_count(res.totalcount);

        // ✅ Capture the Excel download URL from API response
        if (res?.report_url) {
          setExcelUrl(res.report_url);
        } else {
          setExcelUrl(null);
        }
      } else {
        setReports_list([]);
        setTotal_count(0);
        setExcelUrl(null);
      }
    } catch (error) {
      console.error("Failed to fetch purchase report:", error);
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    store.dispatch(get_all_inventory_list());
    store.dispatch(get_all_vendor_list());
  }, []);

  useEffect(() => {
    if (searchParams) {
      get_report_list();
    }
    setInitialValue((page - 1) * rows + 1);
  }, [page, rows, searchParams]);

  const validateSearch = () => {
    if (!startDate) {
      toast.error("Please select a start date");
      return false;
    }
    if (!endDate) {
      toast.error("Please select an end date");
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("End date cannot be before start date");
      return false;
    }
    if (salesType && !selectedItem) {
      toast.error(`Please select a ${salesType === "product" ? "product" : "vendor"}`);
      return false;
    }
    return true;
  };

  const handleSearch = () => {
    if (!validateSearch()) {
      setHasSearched(false);
      setSearchParams(null);
      return;
    }

    setPage(1);
    setHasSearched(true);
    setSearchParams({
      startDate,
      endDate,
      selectedItem,
      salesType,
    });
  };

  // ✅ Handle Excel download
  const handleDownloadExcel = () => {
    if (excelUrl) {
      window.open(excelUrl, "_blank");
    } else {
      toast.warning("Excel file not available yet. Please search first.");
    }
  };

  const handleRowsChange = (e) => {
    setRows(Number(e.target.value));
    setPage(1);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < Math.ceil(total_count / rows)) setPage(page + 1);
  };

  const calculateSerialNumber = (index) => (page - 1) * rows + index + 1;

  const totalPages = Math.max(Math.ceil(total_count / rows), 1);
  const startRecord = total_count > 0 ? (page - 1) * rows + 1 : 0;
  const endRecord = total_count > 0 ? Math.min(page * rows, total_count) : 0;

  return (
    <>
      {/* Header */}
      <Card
        bg="#fff"
        py={3}
        px={5}
        flexDirection={["column", "row"]}
        alignItems={["flex-start", "center"]}
        gap={3}
      >
        <Heading w="full" fontSize="xl">
          Purchase Reports
        </Heading>
        <Spacer display={["none", "block"]} />
      </Card>

      {/* Filters + Search + Download */}
      <Card bg="#fff" py={3} px={5}>
        <Flex direction={["column", "row"]} wrap="wrap" gap={3} mb={3}>
          <CustomSingleInput
            width={["100%", "200px"]}
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <CustomSingleInput
            width={["100%", "200px"]}
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <CustomSingleInput
            width={["100%", "200px"]}
            label="Filter By"
            placeholder="Select Type"
            select
            options={[
              { label: "Vendor", value: "vendor" },
              { label: "Product", value: "product" },
            ]}
            value={salesType}
            onChange={(e) => {
              setSalesType(e.target.value);
              setSelectedItem(null);
            }}
          />
          {salesType && (
            <CustomSingleInput
              width={["100%", "200px"]}
              label={salesType === "product" ? "Product Name" : "Vendor Name"}
              placeholder={`Select ${salesType}`}
              select
              options={salesType === "product" ? product_list : vendor_list}
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
            />
          )}

          {/* ✅ Search + Download Excel Buttons */}
          <Flex gap={2} align="center">
            <CustomButton
              title="Search"
              w={["100%", "auto"]}
              onClick={handleSearch}
            />
            {excelUrl && (
              <CustomButton
                title="Download Excel"
                variant="outline"
                onClick={handleDownloadExcel}
              />
            )}
          </Flex>
        </Flex>
      </Card>

      {/* Data Table */}
      {loading ? (
        <>
        <GasCylinderLoader/>
        </>
      ) : hasSearched ? (
        reports_list?.length === 0 ? (
          <Card justify="center" align="center" h={20}>
            <Text>No matching records found for the selected criteria</Text>
          </Card>
        ) : (
          <Card>
            <TableView
              headData={[
                "S.No.",
                "INV No",
                "INV Date",
                "Product Name",
                "Type",
                "Quantity",
                "Empty Returned",
                "GST Amount",
                "Taxable Amount",
                "Total Amount",
              ]}
              body={reports_list?.map((el, i) => (
                <Tr key={`${el.inv_no}-${i}`}>
                  <Td textAlign="center">{calculateSerialNumber(i)}</Td>
                  <Td textAlign="center">{el.inv_no}</Td>
                  <Td textAlign="center">{el.inv_date}</Td>
                  <Td textAlign="center">{el.product_name}</Td>
                  <Td textAlign="center">{el.type}</Td>
                  <Td textAlign="center">{el.quantity}</Td>
                  <Td textAlign="center">{el.empty_returned}</Td>
                  <Td textAlign="center">{el.gst_amount}</Td>
                  <Td textAlign="center">{el.taxable_amount}</Td>
                  <Td textAlign="center">{el.total_amount}</Td>
                </Tr>
              ))}
              initialValue={initialValue}
              page={page}
              rows={rows}
              total_value={total_count}
              pagination
              onChange={handleRowsChange}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </Card>
        )
      ) : null}
    </>
  );
};

export default PurchaseReports;
